import React, { useState, useEffect } from "react";
import { Form, FormGroup, Label, Row, Col, Spinner } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import SignatureWithName from '../SignatureWithName';
const _ = require("lodash");

// Name/NPI/signature all come from the API (medicalDirectorName / medicalDirectorNPI /
// medicalDirectorSignature on formData); these are the fallback until it responds.
const MEDICAL_DIRECTOR_NAME = "Dr. Daniel Beirne";
const MEDICAL_DIRECTOR_NPI_FALLBACK = "N/A";

const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const toTitleCase = (str) => {
  if (!str) return "";
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

const formatLabel = (str) => {
  if (!str) return "N/A";
  return str;
};

const RPMCarePlanForm = React.forwardRef(({
  formData,
  onInputChange,
  onSubmit,
  onExpectationsChange = null,
  onCancel,
  isEditing = false,
  showButtons = true,
  patientId = null,
  onConditionChange = null,
  selectedExpectations = null,
  isViewMode = false,
  conditionNames = [],
  savedConditionIds = [],
  savedConditionIcdCodes = [],
  onConditionIcdCodesChange = null,
  onConditionIcdCodesValidityChange = null,
  showConditionError = false,
  showIcdCodesError = false,
  rpmMedicalNecessityConfig = [],
  ongoingRPMMedicalNecessityConfigRequest = false
}, ref) => {
  const dispatch = useDispatch();
  // Get RPM conditions and expectations from Redux store
  const {
    rpmPatientConditions = [],
    ongoingRPMPatientConditionsRequest = false,
    rpmPatientIcdCodes = [],
    ongoingRPMPatientIcdCodesRequest = false,
    rpmPatientExpectations = [],
    ongoingRPMPatientExpectationsRequest = false
  } = useSelector((state) => state.careprovider);

  const hasUserInteracted = React.useRef(false);
  const hasAutoSelectedFallback = React.useRef(false);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [expectations, setExpectations] = useState(selectedExpectations || []);

  const OTHER_ICD_CODE_OPTION = { _id: "OTHER", code: "OTHER", label: "Other / Manual Entry" };
  const OTHER_CATEGORY_VALUE = "OTHERCATEGORY";
  const OTHER_CATEGORY_OPTION = { value: OTHER_CATEGORY_VALUE, label: "Other / Manual Entry" };
  const hasIcdRowsInitialized = React.useRef(false);
  const [conditionIcdRows, setConditionIcdRows] = useState([]);

  useEffect(() => {
    dispatch({ type: "DO_GET_RPM_PATIENT_ICD_CODES" });
  }, []);

  useEffect(() => {
    if (!hasIcdRowsInitialized.current && !isViewMode) {
      hasIcdRowsInitialized.current = true;
      if (savedConditionIcdCodes && savedConditionIcdCodes.length > 0) {
        setConditionIcdRows(savedConditionIcdCodes.map((entry, index) => ({
          key: `${Date.now()}-${index}`,
          category: entry.category || null,
          manualCategory: "",
          icdCodeId: entry.icdCodeId && entry.icdCodeId._id
            ? entry.icdCodeId._id
            : (entry.manualIcdCode ? "OTHER" : null),
          manualIcdCode: entry.manualIcdCode || ""
        })));
      }
    }
  }, [savedConditionIcdCodes, isViewMode]);

  const icdCategories = _.uniq((rpmPatientIcdCodes || []).map((c) => c.category)).sort();

  const emitConditionIcdCodesChange = (rows) => {
    if (onConditionIcdCodesChange) {
      const payload = rows
        .map((row) => ({
          ...row,
          effectiveCategory: row.category === OTHER_CATEGORY_VALUE ? (row.manualCategory || "").trim() : row.category
        }))
        .filter((row) => row.effectiveCategory && (row.icdCodeId || row.manualIcdCode))
        .map((row) => ({
          category: row.effectiveCategory,
          icdCodeId: row.icdCodeId && row.icdCodeId !== "OTHER" ? row.icdCodeId : undefined,
          manualIcdCode: (!row.icdCodeId || row.icdCodeId === "OTHER") ? row.manualIcdCode : undefined
        }));
      onConditionIcdCodesChange(payload);
    }
  };

  const handleAddIcdRow = () => {
    const rows = [...conditionIcdRows, { key: `${Date.now()}-${conditionIcdRows.length}`, category: null, manualCategory: "", icdCodeId: null, manualIcdCode: "" }];
    setConditionIcdRows(rows);
  };

  const handleRemoveIcdRow = (key) => {
    const rows = conditionIcdRows.filter((row) => row.key !== key);
    setConditionIcdRows(rows);
    emitConditionIcdCodesChange(rows);
  };

  const handleIcdCategoryChange = (key, category) => {
    const rows = conditionIcdRows.map((row) =>
      row.key === key ? { ...row, category, manualCategory: "", icdCodeId: null, manualIcdCode: "" } : row
    );
    setConditionIcdRows(rows);
    emitConditionIcdCodesChange(rows);
  };

  const handleManualCategoryChange = (key, value) => {
    const rows = conditionIcdRows.map((row) => (row.key === key ? { ...row, manualCategory: value } : row));
    setConditionIcdRows(rows);
    emitConditionIcdCodesChange(rows);
  };

  const handleIcdCodeChange = (key, selectedOption) => {
    const rows = conditionIcdRows.map((row) =>
      row.key === key
        ? { ...row, icdCodeId: selectedOption ? selectedOption._id : null, manualIcdCode: selectedOption && selectedOption._id === "OTHER" ? row.manualIcdCode : "" }
        : row
    );
    setConditionIcdRows(rows);
    emitConditionIcdCodesChange(rows);
  };

  const handleManualIcdCodeChange = (key, value) => {
    const rows = conditionIcdRows.map((row) => (row.key === key ? { ...row, manualIcdCode: value } : row));
    setConditionIcdRows(rows);
    emitConditionIcdCodesChange(rows);
  };

  // A row is incomplete if the user has started it (picked a category, or "Other" for either
  // field) but hasn't finished it. Rows like this are silently dropped from the saved payload,
  // so the parent must block submission until they're completed or removed.
  const getIcdRowValidation = (row) => {
    const isManualCategory = row.category === OTHER_CATEGORY_VALUE;
    const isMissingManualCategory = isManualCategory && !row.manualCategory.trim();
    const hasResolvedCategory = !!row.category && !isMissingManualCategory;
    const isMissingCode = hasResolvedCategory && !row.icdCodeId;
    const isMissingManualCode = row.icdCodeId === "OTHER" && !row.manualIcdCode.trim();
    return {
      isManualCategory,
      isMissingManualCategory,
      isMissingCode,
      isMissingManualCode,
      isIncomplete: isMissingManualCategory || isMissingCode || isMissingManualCode
    };
  };

  useEffect(() => {
    if (onConditionIcdCodesValidityChange) {
      const hasIncompleteRow = conditionIcdRows.some((row) => getIcdRowValidation(row).isIncomplete);
      onConditionIcdCodesValidityChange(!hasIncompleteRow);
    }
  }, [conditionIcdRows]);

  const conditionErrorRef = React.useRef(null);
  const icdCodesErrorRef = React.useRef(null);
  React.useImperativeHandle(ref, () => ({
    scrollToConditionError: () => {
      if (conditionErrorRef.current) {
        conditionErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    scrollToIcdCodesError: () => {
      if (icdCodesErrorRef.current) {
        icdCodesErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }));

  useEffect(() => {
    if (rpmPatientConditions && rpmPatientConditions.length > 0 && !hasUserInteracted.current) {
      if (savedConditionIds && savedConditionIds.length > 0) {
        const savedOnes = rpmPatientConditions.filter(c =>
          savedConditionIds.some(model => {
            const modelId = (model && typeof model === "object") ? model._id : model;
            return String(modelId) === String(c._id);
          })
        );
        setSelectedConditions(savedOnes);
        if (!isViewMode) {
          setExpectations([]);  // ← clear stale expectations before fresh fetch
          if (onExpectationsChange) onExpectationsChange([]);
          const ids = savedOnes.map(c => c._id).filter(Boolean);
          if (ids.length > 0 && patientId) {
            dispatch({ type: "DO_GET_RPM_PATIENT_EXPECTATIONS", payload: { conditionIds: ids, patientId } });
          }
        }
      } else if (!isViewMode && !hasAutoSelectedFallback.current) {
        // No saved conditions to restore — fall back to selecting everything available.
        // This must mirror the branch above: notify the parent and fetch expectations,
        // otherwise the parent's selectedConditionIds stays empty (blocking save) and the
        // Monitoring Parameters & Frequency section never loads for the auto-selected conditions.
        // Guarded to run once: onConditionChange below triggers a parent state update, and if
        // the parent passes an inline `[]` literal for savedConditionIds (a new reference on
        // every render), this effect would otherwise re-fire and loop indefinitely.
        hasAutoSelectedFallback.current = true;
        setSelectedConditions(rpmPatientConditions);
        setExpectations([]);
        if (onExpectationsChange) onExpectationsChange([]);
        const ids = rpmPatientConditions.map(c => c._id).filter(Boolean);
        if (ids.length > 0 && patientId) {
          dispatch({ type: "DO_GET_RPM_PATIENT_EXPECTATIONS", payload: { conditionIds: ids, patientId } });
        }
        if (onConditionChange) {
          onConditionChange(ids);
        }
      }
    }
  }, [rpmPatientConditions, isViewMode, savedConditionIds]);


  const handleConditionSelect = (selectedOptions) => {
    hasUserInteracted.current = true;
    const options = selectedOptions || [];
    setSelectedConditions(options);
    setExpectations([]);  // ← clear immediately so stale data doesn't show
    if (onExpectationsChange) {
      onExpectationsChange([]);  // ← also clear in parent
    }
    const conditionIds = options.map(option => option._id);
    if (conditionIds.length > 0 && patientId) {
      dispatch({
        type: "DO_GET_RPM_PATIENT_EXPECTATIONS",
        payload: { conditionIds, patientId }
      });
    }
    if (onConditionChange) {
      onConditionChange(conditionIds.length > 0 ? conditionIds : []);
    }
  };

  // Update expectations when Redux state changes
  useEffect(() => {
    if (!isViewMode) {
      setExpectations(rpmPatientExpectations);
      if (onExpectationsChange) {
        onExpectationsChange(rpmPatientExpectations);
      }
    }
  }, [rpmPatientExpectations]);

  // Format conditions for Select component
  const sortedConditions = _.sortBy(rpmPatientConditions || [], (obj) => {
    var name = obj.condition || '';
    if (name.toLowerCase().includes("CUSTOM".toLowerCase())) return 1;
    return 0;
  });
  const hasMedicalNecessity = rpmMedicalNecessityConfig && rpmMedicalNecessityConfig.length > 0 && 
    rpmMedicalNecessityConfig.some(config => config.medicalNecessity);

  const sectionNumbers = hasMedicalNecessity ? {
    medicalNecessity: 1,
    patientConsent: 2,
    deviceInfo: 3,
    monitoring: 4,
    clinicalMgmt: 5,
    goalsOfCare: 6,
    rpmGoals: 7,
    careTeam: 8,
  } : {
    patientConsent: 1,
    deviceInfo: 2,
    monitoring: 3,
    clinicalMgmt: 4,
    goalsOfCare: 5,
    rpmGoals: 6,
    careTeam: 7,
  };

  const hasGoals = rpmMedicalNecessityConfig && rpmMedicalNecessityConfig.length > 0 && 
    rpmMedicalNecessityConfig.some(config => config.goals && config.goals.length > 0);

  return (
    <div className="rpm-care-plan-view" style={{ width: '100%', overflowX: 'hidden' }}>
      <Form onSubmit={onSubmit} style={{ width: '100%' }}>
        {/* Patient Information */}
        <div className="section">
          <Row>
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '10px' }}><strong>Patient Name:</strong> {formData.patientName ? toTitleCase(formData.patientName) : "N/A"}</p></Col>
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '10px' }}><strong>DOB:</strong> {formData.dob || "N/A"}</p></Col>
            {/* <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '10px' }}><strong>Primary Diagnosis:</strong> {formData.primaryDiagnosis ? toTitleCase(formData.primaryDiagnosis) : "N/A"}</p></Col> */}
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '10px' }}><strong>Ordering Provider:</strong> {formData.orderingProvider || "N/A"}</p></Col>
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '10px' }}><strong>NPI:</strong> {formData.npi ? toTitleCase(formData.npi) : "N/A"}</p></Col>
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '10px' }}><strong>Medical Director / Supervisor:</strong> {formData.medicalDirectorName || MEDICAL_DIRECTOR_NAME}</p></Col>
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '10px' }}><strong>NPI:</strong> {formData.medicalDirectorNPI || MEDICAL_DIRECTOR_NPI_FALLBACK}</p></Col>
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '0px' }}><strong>Date RPM Initiated:</strong> {formData.dateRPMInitiated ? toTitleCase(formData.dateRPMInitiated) : "N/A"}</p></Col>
            <Col md={6} sm={12}><p style={{ margin: 0, marginBottom: '0px' }}><strong>Last Modified Date:</strong> {formData.lastModifiedDate || "N/A"}</p></Col>
          </Row>
        </div>

        <hr style={{ margin: '5px 0' }} />

        {/* Condition Selection — editable or read-only */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <FormGroup>
                <h4 style={{ fontWeight: 'bold' }}>Primary Diagnosis</h4>
                {isViewMode ? (
                  // Read-only view: show condition names as plain text
                  <div>
                    {conditionNames && conditionNames.length > 0 ? (
                      <ul style={{ marginLeft: '15px', marginTop: '4px', marginBottom: 0 }}>
                        {conditionNames.map((name, i) => {
                          const parenIndex = name.indexOf(' (');
                          const conditionName = parenIndex > -1 ? name.substring(0, parenIndex) : name;
                          const description = parenIndex > -1 ? name.substring(parenIndex) : '';
                          return (
                            <li key={i}>
                              <strong>{toTitleCase(conditionName)}</strong>
                              {description && <span style={{ fontWeight: 'normal' }}>{description}</span>}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p style={{ margin: 0, color: '#666' }}>No conditions recorded</p>
                    )}
                  </div>
                ) : (
                  // Editable dropdown
                  <>
                    <Select
                      SelectComponent={Select}
                      onChange={handleConditionSelect}
                      options={sortedConditions}
                      isMulti={true}
                      isClearable={true}
                      isDisabled={ongoingRPMPatientConditionsRequest}
                      placeholder="-- Select Conditions --"
                      getOptionLabel={(option) =>
                        `${toTitleCase(option.condition)} (${option.description})`
                      }
                      getOptionValue={(option) => option._id}
                      value={selectedConditions}
                    />
                    {ongoingRPMPatientConditionsRequest && (
                      <p style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>Loading conditions...</p>
                    )}
                      {!ongoingRPMPatientConditionsRequest && selectedConditions.length === 0 && (
                        <p
                          ref={conditionErrorRef}
                          style={{ marginTop: '8px', marginBottom: 0, color: '#d9534f', fontSize: '13px', fontWeight: showConditionError ? 'bold' : 'normal' }}
                        >
                          {showConditionError ? '⚠️ Please select one or more conditions before saving.' : 'Please select one or more conditions'}
                        </p>
                      )}
                  </>
                )}
              </FormGroup>
            </Col>
          </Row>
        </div>

        {/* ICD-10 Diagnosis Codes — cascading category -> code selection, or read-only display */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <FormGroup style={{ marginBottom: 0 }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: isViewMode ? 8 : 16 }}>
                  <i className="mdi mdi-clipboard-pulse-outline" style={{ marginRight: 6, color: '#2f7ed8' }}></i>
                  ICD-10 Diagnosis Codes
                </h4>
                {isViewMode ? (
                  <div>
                    {formData.conditionIcdCodes && formData.conditionIcdCodes.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {formData.conditionIcdCodes.map((entry, i) => {
                          const code = entry.icdCodeId && entry.icdCodeId.code ? entry.icdCodeId.code : entry.manualIcdCode;
                          const label = entry.icdCodeId && entry.icdCodeId.label ? entry.icdCodeId.label : null;
                          return (
                            <div
                              key={i}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '10px 14px',
                                background: '#f7f9fc',
                                border: '1px solid #e3e8f0',
                                borderRadius: 8
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  letterSpacing: '0.02em',
                                  color: '#5b7391',
                                  textTransform: 'uppercase',
                                  background: '#e7edf6',
                                  borderRadius: 6,
                                  padding: '3px 8px',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {entry.category}
                              </span>
                              <span style={{ color: '#1f2937' }}>
                                <strong>{code}</strong>
                                {label ? <span style={{ color: '#6b7280' }}> — {label}</span> : null}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p style={{ margin: 0, color: '#8792a2' }}>No ICD-10 codes recorded</p>
                    )}
                  </div>
                ) : (
                  <>
                    {conditionIcdRows.length === 0 && !ongoingRPMPatientIcdCodesRequest && (
                      <div
                        style={{
                          border: '1.5px dashed #cbd5e1',
                          borderRadius: 10,
                          padding: '20px 16px',
                          textAlign: 'center',
                          color: '#8792a2',
                          marginBottom: 14
                        }}
                      >
                        <i className="mdi mdi-clipboard-text-search-outline" style={{ fontSize: 28, display: 'block', marginBottom: 6, color: '#b7c1d1' }}></i>
                        No ICD-10 codes added yet
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {conditionIcdRows.map((row, index) => {
                        const codeOptions = row.category
                          ? [...rpmPatientIcdCodes.filter((c) => c.category === row.category), OTHER_ICD_CODE_OPTION]
                          : [];
                        const selectedCodeOption = codeOptions.find((c) => c._id === row.icdCodeId) || null;
                        const {
                          isManualCategory,
                          isMissingManualCategory,
                          isMissingCode,
                          isMissingManualCode,
                          isIncomplete
                        } = getIcdRowValidation(row);
                        const FIELD_FONT_SIZE = 14;
                        const selectStyles = {
                          control: (base, state) => ({
                            ...base,
                            minHeight: 42,
                            borderRadius: 8,
                            fontSize: FIELD_FONT_SIZE,
                            borderColor: state.isFocused ? '#2f7ed8' : '#d7dee8',
                            boxShadow: state.isFocused ? '0 0 0 1px #2f7ed8' : 'none',
                            '&:hover': { borderColor: '#2f7ed8' }
                          }),
                          placeholder: (base) => ({ ...base, fontSize: FIELD_FONT_SIZE }),
                          singleValue: (base) => ({ ...base, fontSize: FIELD_FONT_SIZE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                          input: (base) => ({ ...base, fontSize: FIELD_FONT_SIZE }),
                          option: (base) => ({ ...base, fontSize: FIELD_FONT_SIZE }),
                          menu: (base) => ({ ...base, zIndex: 5 })
                        };
                        const manualInputStyle = {
                          borderRadius: 8,
                          fontSize: FIELD_FONT_SIZE,
                          borderColor: '#d7dee8'
                        };
                        return (
                          <div
                            key={row.key}
                            style={{
                              position: 'relative',
                              border: `1px solid ${isIncomplete ? '#fbd0ce' : '#e3e8f0'}`,
                              borderLeft: `4px solid ${isIncomplete ? '#d92d20' : '#2f7ed8'}`,
                              borderRadius: 10,
                              padding: '16px 44px 16px 18px',
                              background: isIncomplete ? '#fffbfa' : '#fff',
                              boxShadow: '0 1px 2px rgba(16,24,40,0.04)'
                            }}
                          >
                            <button
                              type="button"
                              title="Remove this diagnosis code"
                              onClick={() => handleRemoveIcdRow(row.key)}
                              style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                border: 'none',
                                background: 'transparent',
                                color: '#b0233d',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = '#fdecee'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              <i className="mdi mdi-trash-can-outline" style={{ fontSize: 17 }}></i>
                            </button>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                color: isIncomplete ? '#d92d20' : '#8792a2',
                                textTransform: 'uppercase',
                                marginBottom: 10,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6
                              }}
                            >
                              Diagnosis {index + 1}
                              {isIncomplete && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 13 }}></i>
                                  Incomplete
                                </span>
                              )}
                            </div>
                            <div style={{ marginBottom: 12 }} title={row.category && !isMissingManualCategory ? row.category : ''}>
                              <Label style={{ fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>
                                Chronic Condition Category
                              </Label>
                              <Select
                                placeholder="Select a category..."
                                options={[...icdCategories.map((c) => ({ value: c, label: c })), OTHER_CATEGORY_OPTION]}
                                isClearable={true}
                                isDisabled={ongoingRPMPatientIcdCodesRequest}
                                styles={selectStyles}
                                value={
                                  row.category === OTHER_CATEGORY_VALUE
                                    ? OTHER_CATEGORY_OPTION
                                    : (row.category ? { value: row.category, label: row.category } : null)
                                }
                                onChange={(option) => handleIcdCategoryChange(row.key, option ? option.value : null)}
                              />
                              {isManualCategory && (
                                <div style={{ marginTop: 8 }}>
                                  <Label style={{ fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>
                                    Manual Chronic Condition Category
                                  </Label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    style={{
                                      ...manualInputStyle,
                                      borderColor: isMissingManualCategory ? '#d92d20' : '#d7dee8'
                                    }}
                                    placeholder="e.g. Sleep Apnea"
                                    value={row.manualCategory}
                                    onChange={(e) => handleManualCategoryChange(row.key, e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                            <div
                              style={{ marginBottom: 0 }}
                              title={selectedCodeOption && selectedCodeOption._id !== 'OTHER' ? `${selectedCodeOption.code} — ${selectedCodeOption.label}` : ''}
                            >
                              <Label style={{ fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>
                                ICD-10 Code
                              </Label>
                              <Select
                                placeholder={row.category ? "Select a code..." : "Select a category first"}
                                options={codeOptions}
                                isClearable={true}
                                isDisabled={!row.category}
                                styles={selectStyles}
                                getOptionLabel={(option) => option._id === "OTHER" ? option.label : `${option.code} — ${option.label}`}
                                getOptionValue={(option) => option._id}
                                value={selectedCodeOption}
                                onChange={(option) => handleIcdCodeChange(row.key, option)}
                              />
                              {row.icdCodeId === "OTHER" && (
                                <div style={{ marginTop: 8 }}>
                                  <Label style={{ fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>
                                    Manual ICD-10 Code
                                  </Label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    style={{
                                      ...manualInputStyle,
                                      borderColor: isMissingManualCode ? '#d92d20' : '#d7dee8',
                                      maxWidth: 320
                                    }}
                                    placeholder="e.g. I10"
                                    value={row.manualIcdCode}
                                    onChange={(e) => handleManualIcdCodeChange(row.key, e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                            {isIncomplete && (
                              <p style={{ margin: '10px 0 0', fontSize: 12, color: '#d92d20', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <i className="mdi mdi-information-outline" style={{ fontSize: 14 }}></i>
                                {isMissingManualCategory
                                  ? "Enter the chronic condition category above — this diagnosis won't be saved until you do."
                                  : isMissingManualCode
                                    ? "Enter the ICD-10 code above — this diagnosis won't be saved until you do."
                                    : "Select an ICD-10 code, or choose \"Other / Manual Entry\" — this diagnosis won't be saved until you do."}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddIcdRow}
                      style={{
                        marginTop: 14,
                        width: '100%',
                        padding: '10px 16px',
                        borderRadius: 8,
                        border: '1.5px dashed #2f7ed8',
                        background: '#f5f9ff',
                        color: '#2f7ed8',
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#eaf2ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#f5f9ff'; }}
                    >
                      <i className="mdi mdi-plus-circle-outline" style={{ fontSize: 18 }}></i>
                      Add ICD-10 Code
                    </button>
                    {ongoingRPMPatientIcdCodesRequest && (
                      <p style={{ marginTop: '8px', color: '#8792a2', fontSize: '12px' }}>Loading ICD-10 codes...</p>
                    )}
                    {showIcdCodesError && (
                      <p
                        ref={icdCodesErrorRef}
                        style={{ marginTop: '8px', marginBottom: 0, color: '#d9534f', fontSize: '13px', fontWeight: 'bold' }}
                      >
                        ⚠️ Please complete or remove the incomplete diagnosis code(s) above before saving.
                      </p>
                    )}
                  </>
                )}
              </FormGroup>
            </Col>
          </Row>
        </div>

        {/* Medical Necessity — displayed based on API response */}
        {ongoingRPMMedicalNecessityConfigRequest ? (
          <div className="text-center" style={{ padding: '20px' }}>
            <Spinner color="primary" size="sm" />
            <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>Loading medical necessity details...</p>
          </div>
        ) : rpmMedicalNecessityConfig && rpmMedicalNecessityConfig.length > 0 ? (
          <>
            <hr style={{ margin: '10px 0' }} />
            <div className="section">
              <Row>
                <Col md={12} sm={12}>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>{sectionNumbers.medicalNecessity}. Medical Necessity Statement</h4>
                  <ul style={{ marginLeft: '20px' }}>
                    {rpmMedicalNecessityConfig.map((config, index) => (
                      config.medicalNecessity ? (
                        <li key={index} style={{ marginBottom: '10px' }}>
                          {config.medicalNecessity.format === 'html' ? (
                            <span dangerouslySetInnerHTML={{ __html: config.medicalNecessity.content }} />
                          ) : (
                            <span style={{ whiteSpace: 'pre-line' }}>{config.medicalNecessity.content}</span>
                          )}
                        </li>
                      ) : null
                    ))}
                  </ul>
                </Col>
              </Row>
            </div>
          </>
        ) : !ongoingRPMMedicalNecessityConfigRequest ? (
          <div style={{ padding: '10px', color: '#666' }}>No medical necessity config found for selected conditions.</div>
        ) : null}

        <hr style={{ margin: '5px 0' }} />

        {/* Patient Consent */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>{sectionNumbers.patientConsent}. Patient Consent</h4>
              <p style={{ margin: 0 }}>Verbal consent obtained on <strong>{formData.consentDate || "N/A"}</strong>. Patient informed of:</p>
              <ul style={{ marginLeft: '40px' }}>
                <li>Nature of RPM services</li>
                <li>Cost-sharing obligations (if applicable)</li>
                <li>Data collection and transmission process</li>
                <li>Right to withdraw at any time</li>
              </ul>
              {/* Patient Consent Signature */}
              <div style={{ marginTop: '10px' }}>
                <SignatureWithName
                  signature={formData.patientSignature}
                  isBorder={true}
                  name={formData.patientName ? toTitleCase(formData.patientName) : ""}
                  isCCMQuestion={false}
                  style={{ float: 'left' }}
                  layoutStyle={{ justifyContent: 'start' }}
                  noSignatureMessage={formData.hasVerbalConsentNote ? "Verbal Consent Documented in Patient's Chart Notes" : undefined}
                />
              </div>
            </Col>
          </Row>
        </div>

        <hr style={{ margin: '10px 0' }} />

        {/* Device Information */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <h4 style={{ fontWeight: 'bold', marginBottom: '5px' }}>{sectionNumbers.deviceInfo}. Device Information (99453/99454 Compliance)</h4>
              <p style={{ marginBottom: 0, marginTop: '10px' }}><strong>Device Provided: (FDA-cleared, automatic transmission)</strong></p>
              {formData.deviceInfo && (
                <ul style={{ marginLeft: '15px', marginTop: '5px' }}>
                  {formData.deviceInfo.split(', ').map((device, index) => (
                    <li key={index}>{toTitleCase(device)}</li>
                  ))}
                </ul>
              )}
              <p style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: 0 }}>
                Setup &amp; Education Completed{formData.setupEducationDate ? ` on ${formData.setupEducationDate}` : ""} (99453):
              </p>
              <ul style={{ marginLeft: '15px', marginTop: '5px' }}>
                <li>Device pairing and demonstration</li>
                <li>Education on correct BP technique</li>
                <li>Troubleshooting guidance</li>
                <li>Written instructions provided</li>
              </ul>
            </Col>
          </Row>
        </div>

        <hr style={{ margin: '5px 0' }} />

        {/* Monitoring Parameters & Frequency */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <h4 style={{ fontWeight: 'bold'}}>{sectionNumbers.monitoring}. Monitoring Parameters & Frequency</h4>

              {/* --- VIEW MODE: render from formData.expectationThreshold --- */}
              {isViewMode ? (
                formData.expectationThreshold && formData.expectationThreshold.length > 0 ? (
                  <div>
                     <div style={{ marginLeft: '15px'}}>
                      {(() => {
                        const grouped = _.groupBy(
                          formData.expectationThreshold.filter(item => item.description && item.description !== 'undefined' && item.description !== undefined),
                          "description"
                        );
                        return Object.keys(grouped).map((readingTypeName, typeIndex) => {
                          const subThresholds = grouped[readingTypeName];
                          return (
                            <div key={typeIndex}>
                              <p style={{ margin: '0px', fontWeight: 'bold', fontSize: '16px' }}>
                                {capitalizeFirstLetter(readingTypeName)}:
                              </p>
                              <ul style={{ marginLeft: '20px', marginTop: '0px', marginBottom: '0px' }}>
                                <li style={{ marginBottom: '4px' }}>
                                  <strong>Frequency:</strong> Daily (minimum 2 days with a reading/month required for 99445) and (minimum 16 days with a reading/month required for 99454)
                                </li>
                                <li style={{ marginBottom: '0px' }}>
                                  <strong>Alert Thresholds:</strong>
                                </li>
                              </ul>
                              <ul style={{ marginLeft: '50px', marginTop: '4px', marginBottom: '0px' }}>
                                {subThresholds.map((subThreshold, subIndex) => {
                                  const subTypeName = subThreshold.subTypes || subThreshold.abbrev || 'Parameter';
                                  const lowest = subThreshold.lowestValue;
                                  const highest = subThreshold.highestValue;
                                  const gainLoss = subThreshold.gainLossPerDay;
                                  let alertThreshold = '';
                                  if (gainLoss !== undefined && gainLoss !== null && gainLoss !== '') {
                                    alertThreshold = `Alert if gain/loss > ${gainLoss} lbs/day`;
                                  } else if (lowest !== undefined && lowest !== null && highest !== undefined && highest !== null) {
                                    alertThreshold = `> ${lowest} or < ${highest}`;
                                  } else if (lowest !== undefined && lowest !== null) {
                                    alertThreshold = `> ${lowest}`;
                                  } else if (highest !== undefined && highest !== null) {
                                    alertThreshold = `< ${highest}`;
                                  } else {
                                    alertThreshold = 'As per protocol';
                                  }
                                  return (
                                    <li key={subIndex}>
                                      {subTypeName}: {alertThreshold}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                ) : (
                  <p style={{ color: '#666', marginTop: '10px' }}>No monitoring parameters recorded.</p>
                )

                /* --- ADD MODE: render from selected conditions/expectations --- */
              ) : selectedConditions && selectedConditions.length > 0 ? (
                <div>
                  {ongoingRPMPatientExpectationsRequest ? (
                    <p style={{ marginTop: '8px', marginLeft: '15px', color: '#666', fontSize: '12px' }}>⏳ Loading expectations for selected conditions...</p>
                  ) : expectations && expectations.length > 0 ? (
                    <div style={{ marginLeft: '15px', marginTop: '10px' }}>
                      {(() => {
                        const grouped = _.groupBy(
                          expectations.filter(item => item.description && item.description !== 'undefined' && item.description !== undefined),
                          "description"
                        );
                        return Object.keys(grouped).map((readingTypeName, typeIndex) => {
                          const subThresholds = grouped[readingTypeName];
                          return (
                            <div key={typeIndex} style={{ marginBottom: '0px', paddingBottom: '0px' }}>
                              <p style={{ margin: '0px', fontWeight: 'bold', fontSize: '16px' }}>
                                {capitalizeFirstLetter(readingTypeName)}:
                              </p>
                              <ul style={{ marginLeft: '20px', marginTop: '0px', marginBottom: '0px' }}>
                                <li style={{ marginBottom: '8px' }}>
                                  <strong>Frequency:</strong> Daily (minimum 2 days with a reading/month required for 99445) and (minimum 16 days with a reading/month required for 99454)
                                </li>
                                <li style={{ marginBottom: '0px' }}>
                                  <strong>Alert Thresholds:</strong>
                                </li>
                              </ul>
                              <ul style={{ marginLeft: '50px', marginTop: '5px', marginBottom: '0px' }}>
                                {subThresholds.map((subThreshold, subIndex) => {
                                  const subTypeName = subThreshold.subTypes || subThreshold.abbrev || 'Parameter';
                                  const lowest = subThreshold.lowestValue;
                                  const highest = subThreshold.highestValue;
                                  let alertThreshold = '';
                                  if (lowest !== undefined && lowest !== null && highest !== undefined && highest !== null) {
                                    alertThreshold = `> ${lowest} or < ${highest}`;
                                  } else if (lowest !== undefined && lowest !== null) {
                                    alertThreshold = `> ${lowest}`;
                                  } else if (highest !== undefined && highest !== null) {
                                    alertThreshold = `< ${highest}`;
                                  } else {
                                    alertThreshold = 'As per protocol';
                                  }
                                  return (
                                    <li key={subIndex} style={{ marginBottom: '5px' }}>
                                      {subTypeName}: {alertThreshold}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <p style={{ marginLeft: '15px', marginTop: '8px', color: '#d9534f', fontSize: '12px' }}>⚠️ No expectations found for selected conditions.</p>
                  )}
                </div>
              ) : (null)}
            </Col>
          </Row>
        </div>

        <hr style={{ margin: '1px 0' }} />

        {/* Clinical Management Protocol */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>{sectionNumbers.clinicalMgmt}. Clinical Management Protocol (99457/99458)</h4>
                <p style={{ margin: 0 }}>Clinical staff under general supervision of physician will:</p>
              <ul>
                <li>Review transmitted data weekly</li>
                <li>Identify trends or abnormal readings</li>
                <li>Document clinical interpretation</li>
                <li>Contact patient if alert threshold triggered</li>
                <li>Reinforce medication adherence</li>
                <li>Provide diet and lifestyle counseling</li>
                <li>Escalate to provider when necessary</li>
              </ul>
              <p><strong>Provider's Preferred Escalation Plan</strong></p>
              <ul>
                <li>Critical Value = <strong>{formData.escalationPlan?.criticalValue ? toTitleCase(formData.escalationPlan?.criticalValue) : "N/A"}</strong></li>
                <li>Persistent Elevation = <strong>{formData.escalationPlan?.persistentElevation ? toTitleCase(formData.escalationPlan?.persistentElevation) : "N/A"}</strong></li>
                <li>Medication adjustment documented in EHR</li>
                <li>ER referral if urgency / emergency suspected</li>
              </ul>
            </Col>
          </Row>
        </div>

        <hr style={{ margin: '5px 0' }} />

        {/* Goals of Care */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>{sectionNumbers.goalsOfCare}. Goals of Care</h4>
              <ul>
                <li>Blood Pressure Goal: <strong>{toTitleCase(formData.goalsOfCare?.bloodPressureGoal) || "N/A"}</strong></li>
                <li>Glucose Goal: <strong>{toTitleCase(formData.goalsOfCare?.glucoseGoal) || "N/A"}</strong></li>
                <li>Weight Goal: <strong>{toTitleCase(formData.goalsOfCare?.weightGoal) || "N/A"}</strong></li>
                <li>Blood Oxygen Goal: <strong>{toTitleCase(formData.goalsOfCare?.bloodOxygenGoal) || "N/A"}</strong></li>
                <li>Temperature Goal: <strong>{toTitleCase(formData.goalsOfCare?.temperatureGoal) || "N/A"}</strong></li>
                <li>Activity Goal: <strong>{toTitleCase(formData.goalsOfCare?.activityGoal) || "N/A"}</strong></li>
                <li>Medication Adherence: <strong>{toTitleCase(formData.goalsOfCare?.medicationAdherence) || "N/A"}</strong></li>
                <li>Hospitalizations: <strong>{toTitleCase(formData.goalsOfCare?.hospitalizations) || "N/A"}</strong></li>
              </ul>
            </Col>
          </Row>
        </div>
        {/* RPM Goals - displayed if goals exist in medical necessity config */}
        {hasGoals && sectionNumbers.rpmGoals && (
          <>
            <hr style={{ margin: '5px 0' }} />
            <div className="section">
              <Row>
                <Col md={12} sm={12}>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>{sectionNumbers.rpmGoals}. Goals for RPM Management</h4>
                  <ul style={{ marginLeft: '20px' }}>
                    {rpmMedicalNecessityConfig.map((config, index) => (
                      config.goals && config.goals.length > 0 ? (
                        config.goals.map((goal, goalIndex) => (
                          <li key={`${index}-${goalIndex}`}>{goal}</li>
                        ))
                      ) : null
                    ))}
                  </ul>
                </Col>
              </Row>
            </div>
          </>
        )}

        <hr style={{ margin: '5px 0' }} />
        {/* Care Team */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <h4 style={{ fontWeight: 'bold', marginBottom: '15px' }}>{sectionNumbers.careTeam}. Care Team</h4>
              <ul>
                <li><strong>Supervising Physician:</strong> {formData.careTeam?.supervisingPhysician ? toTitleCase(formData.careTeam?.supervisingPhysician) : "N/A"}</li>
                <li>
                  <strong>RPM Designated Clinical Staff Members:</strong> {formData.careTeam?.rpmDesignatedNames ? toTitleCase(formData.careTeam?.rpmDesignatedNames) : "N/A"}
                </li>
                <li>
                  <strong>RPM Staff Team:</strong> {formData.careTeam?.rpmStaffTeamNames ? toTitleCase(formData.careTeam?.rpmStaffTeamNames) : "N/A"}
                </li>
                <li><strong>Care Coordinator:</strong> {formData.careTeam?.careCoordinator ? toTitleCase(formData.careTeam?.careCoordinator) : "N/A"}</li>
              </ul>
            </Col>
          </Row>
        </div>        

        <hr style={{ margin: '5px 0' }} />

        {/* Provider Signature */}
        <div className="section">
          <Row>
            <Col md={12} sm={12}>
              <p style={{ margin: 0, marginBottom: '8px', textAlign: 'right',paddingRight: '85px' }}>
                <strong>Care Provider Signature:</strong> {formData.signatureDate || "N/A"}
              </p>
              <SignatureWithName
                signature={formData.providerSignature}
                isBorder={true}
                name={formData.orderingProvider || ""}
                noSignatureMessage="Signature not legible"
                isCCMQuestion={false}
                style={{ float: 'left' }}
                layoutStyle={{ justifyContent: 'end' }}
              />
              <p style={{ margin: 0, marginBottom: '8px', marginTop: '15px', textAlign: 'right', paddingRight: '85px' }}>
                <strong>Medical Director / Supervisor Signature:</strong> {formData.signatureDate || "N/A"}
              </p>
              <SignatureWithName
                signature={formData.medicalDirectorSignature}
                isBorder={true}
                name={formData.medicalDirectorName || MEDICAL_DIRECTOR_NAME}
                noSignatureMessage="Signature not legible"
                isCCMQuestion={false}
                style={{ float: 'left' }}
                layoutStyle={{ justifyContent: 'end' }}
              />
            </Col>
          </Row>
        </div>
      </Form>
    </div>
  );
});

export default RPMCarePlanForm;